import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Alert } from "reactstrap";
import Form from "src/Components/Form/index";
import ContentLoading from "src/Components/Loading/contentLoading";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import {
  useGetTotalRewardsPerkCategory,
  useSaveTotalRewardsPerkCategory,
} from "src/Pages/Company/TotalReward/api/usePerksCategories";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import newRelicErrorReport from "src/utils/newRelic/newRelicErrorReport";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import useIsImpersonated from "src/utils/hooks/useIsImpersonated";

export default function TotalRewardCategoriesForm({
  categoryId,
  listingUrl,
  setIsEdited,
}) {
  const isImpersonated = useIsImpersonated();
  const history = useHistory();
  const isNew = categoryId === "-1";
  const companyId = useCompanyId();

  const [originData, setOriginData] = useState();
  const [data, setData] = useState({
    status: true,
  });

  const {
    data: perkCategory,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useGetTotalRewardsPerkCategory(categoryId);

  useEffect(() => {
    if (isSuccess) {
      setOriginData(perkCategory);

      setData({
        id: perkCategory.id,
        description: perkCategory.description,
        name: perkCategory.name,
        sort: perkCategory.sort,
        status: perkCategory.status,
      });
    }
  }, [isSuccess, perkCategory]);

  useEffect(() => {
    if (isError && error?.message) {
      setErrorMessage(error?.message);
    }
  }, [error?.message, isError]);

  const onChange = (key, value) => {
    setData((prevData) => {
      const updatedData = { ...prevData };
      updatedData[key] = value;
      return updatedData;
    });
    setIsEdited(true);
  };

  const [errorMessage, setErrorMessage] = useState(null);

  const mutation = useSaveTotalRewardsPerkCategory(companyId);

  const formApiRef = useRef(null);

  const onSubmit = async () => {
    if (isImpersonated) {
      return;
    }
    setErrorMessage(null);
    await mutation.mutateAsync(data, {
      onSuccess: () => {
        dynamicNotification(__("Pomyślnie zapisano dane kategorii."));
        setIsEdited(false, () => {
          history.push(listingUrl);
        });
      },
      onError: (error) => {
        if (error?.response?.data?.message) {
          setErrorMessage(error.response.data.message);
        } else if (error?.response?.data?.messages?.some((m) => m?.field)) {
          setErrorMessage(__("Popraw błędy w formularzu i spróbuj ponownie."));
        }
        error?.response?.data?.messages?.forEach(({ field, message }) => {
          formApiRef.current?.setError(field, message);
        });
        dynamicNotification(__("Nie udało się zapisać kategorii."), "error");
      },
    });
  };

  return (
    <>
      <Alert
        color="danger"
        isOpen={!!errorMessage}
        toggle={() => setErrorMessage(null)}
      >
        {errorMessage}
      </Alert>
      <ContentLoading
        show={isLoading}
        messageStyle={{ position: "absolute", top: "20%", right: "50%" }}
      >
        <Form
          id="totalRewardCategoryForm"
          apiRef={formApiRef}
          data={data || {}}
          config={{
            title: isNew ? __("Tworzenie kategorii") : __("Edycja kategorii"),
            stickyTitle: true,
            defaultOnChange: onChange,
            onSubmit,
            buttons: [
              {
                size: "lg",
                color: "light",
                className: "mr-2",
                text: "Wróć",
                id: "categoryFormBack",
                onClick: () => {
                  history.push(listingUrl);
                },
              },
              {
                size: "lg",
                color: "primary",
                className: "mr-2",
                text: "Zapisz",
                disabled: isImpersonated,
                type: "submit",
                id: "categoryFormSubmit",
              },
            ],
            formGroups: [
              {
                formElements: [
                  {
                    type: "boolean",
                    id: "status",
                    label: "Aktywna",
                  },
                  {
                    type: "text",
                    id: "name",
                    label: "Nazwa",
                    validation: [
                      "required",
                      { method: "maxLength", args: [255] },
                    ],
                    translatable: isNew
                      ? null
                      : {
                          show: true,
                          code: `totalRewardStatement:perkCategoryObject:${categoryId}:name`,
                          isCms: true,
                        },
                    tooltip: {
                      type: "info",
                      content: (
                        <>{__(`Maksymalna liczba znaków to {0}`, ["255"])}</>
                      ),
                    },
                  },
                  {
                    type: "wysiwyg",
                    id: "description",
                    label: "Opis",
                    validation: ["required"],
                    translatable: isNew
                      ? null
                      : {
                          show: true,
                          code: `totalRewardStatement:perkCategoryObject:${categoryId}:description`,
                          isCms: true,
                        },
                  },
                  {
                    type: "number",
                    id: "sort",
                    label: "Kolejność wyświetlania",
                    validation: [
                      "required",
                      { method: "greaterEqualThan", args: [0] },
                    ],
                  },
                ],
              },
            ],
          }}
        />
      </ContentLoading>
    </>
  );
}

TotalRewardCategoriesForm.propTypes = {
  categoryId: PropTypes.string.isRequired,
  listingUrl: PropTypes.string.isRequired,
  setIsEdited: PropTypes.func,
};

TotalRewardCategoriesForm.defaultProps = {
  setIsEdited: () => {},
};
